import { useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { BackendRoute } from '../config/index';
import { QueryKey } from '../constants/index';
import { ApiService } from '../services/index';
import { accessTokenStorage } from '../storage/auth/index';
import { selectedCompanyStorage } from '../storage/company/selected-company.util';
import { Company } from '../types/company.types';
import { useAppQuery } from './use-app-query.hook';
import { useLocale } from './use-locale.hook';

export const useSelectedCompany = () => {
  const navigate = useNavigate();
  const { locale } = useLocale();
  const accessToken = accessTokenStorage(({ value }) => value);
  const setCompany = selectedCompanyStorage(({ setCompany: applyCompany }) => applyCompany);
  const setCompanyId = selectedCompanyStorage(({ setCompanyId: applyCompanyId }) => applyCompanyId);
  const {
    data: selectedCompany,
    isLoading,
    isFetched,
    refetch,
  } = useAppQuery({
    refetchOnReconnect: 'always',
    refetchOnWindowFocus: true,
    retry: false,
    queryKey: [QueryKey.GET_SELECTED_COMPANY],
    queryFn: async () =>
      (await ApiService.get<Company | null>(
        {
          endpoint: BackendRoute.COMPANIES,
          routePath: ['selected'],
        },
        { locale },
      )) ?? null,
    enabled: !!accessToken,
    onError: () => navigate('/error-page'),
  });

  useLayoutEffect(() => {
    if (!selectedCompany?.id) {
      return;
    }

    setCompanyId(selectedCompany.id);
    setCompany(selectedCompany);
  }, [selectedCompany, setCompany, setCompanyId]);

  // Query cache stores `null` (React Query forbids undefined). Callers still destructure
  // `selectedCompany: { currency } = {}`, and that default only applies to `undefined`.
  return { selectedCompany: selectedCompany ?? undefined, isLoading, isFetched, refetch };
};
